import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Leaf, ClipboardCheck, FileText, Link2, TrendingUp, Target,
  SlidersHorizontal, HelpCircle, BarChart3, FolderLock, History, Sparkles,
  Building2, UserCheck, CalendarRange, Settings, LogOut, Menu, Plus, ChevronDown,
  ShieldCheck, ClipboardList, Zap, Gauge, Users2, Landmark, Scale, Inbox, Upload,
  Wrench, Compass, ArrowLeftRight, ChevronRight, Activity, ScrollText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSelector from "@/components/LanguageSelector";
import FirstLanguageDialog from "@/components/FirstLanguageDialog";
import { NAV_GROUPS, NAV_ITEMS } from "@/lib/navConfig";
import { ROLE_LABELS, PERIOD_STATUS, formatDisplayName } from "@/lib/strings";
import { SizeBadge, DemoBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SectionHint } from "@/components/SectionHint";
import { useConsultantTour } from "@/components/ConsultantTour";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CompanyWizardDialog } from "@/components/CompanyWizardDialog";

const ICONS = {
  LayoutDashboard, Leaf, ClipboardCheck, FileText, Link2, TrendingUp, Target,
  SlidersHorizontal, HelpCircle, BarChart3, FolderLock, History, Sparkles,
  Building2, UserCheck, CalendarRange, Settings, ShieldCheck, ClipboardList, Zap, Gauge, Users2, Landmark, Scale,
  Compass,
};

// Menu guidato ESCLUSIVAMENTE dalle effective capabilities del backend
// (GET /api/auth/capabilities): una voce compare solo se permission=true,
// module_enabled=true e prerequisito soddisfatto. Il ruolo non viene mai
// letto da localStorage; nascondere una voce non è un'autorizzazione: i
// controlli server-side sugli endpoint restano obbligatori.
function NavItems({ onNavigate, showAdmin }) {
  const { user } = useAuth();
  const { caps, companyId } = useTenant();
  const { t } = useI18n();
  const location = useLocation();
  const [badges, setBadges] = useState({});
  const [openSection, setOpenSection] = useState(null);

  // Preferenza server-side: ultima sezione aperta.
  useEffect(() => {
    api.get("/auth/preferences")
      .then(({ data }) => setOpenSection(data.preferences?.nav_open_section || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!companyId) { setBadges({}); return; }
    api.get(`/companies/${companyId}/menu-badges`)
      .then(({ data }) => setBadges(data.badges || {}))
      .catch(() => {});
  }, [companyId]);

  const ctx = caps?.context || {};
  const isConsultantCtx = ["CONSULENTE", "ORG_ADMIN"].includes(ctx.platform_level)
    && (ctx.active_context || "CONSULTANT") === "CONSULTANT";
  const perms = useMemo(() => new Set(caps?.permissions || []), [caps]);
  const modules = caps?.modules || {};
  const prereq = caps?.prerequisites || {};

  const capOk = (item) => {
    if (!caps) return false; // fail-safe: capability non caricate → voce nascosta
    if (item.requiredCapability && !perms.has(item.requiredCapability)) return false;
    if (item.requiredModule && !modules[item.requiredModule]) return false;
    if (item.prerequisite && !prereq[item.prerequisite]?.ok) return false;
    return true;
  };

  const sections = useMemo(() => {
    if (!caps) return [];
    // Super Admin senza support access attivo: mai funzioni ESG aziendali
    // (punto 3-4 IAM: i contenuti ESG delle aziende non sono visibili di default).
    if (ctx.platform_level === "SUPER_ADMIN" && ctx.role === "SUPER_ADMIN") return [];
    const byGroup = (gid, items) => ({ ...NAV_GROUPS.find((g) => g.id === gid), items });
    const out = [];
    if (isConsultantCtx) {
      // Navigazione principale consulente: workspace + voci capability-gated.
      const main = NAV_ITEMS
        .filter((i) => i.group === "consulente" && (i.workspace || capOk(i)))
        .sort((a, b) => a.order - b.order);
      if (main.length) out.push(byGroup("consulente", main));
      // Gruppo secondario: solo funzioni con capability effettiva sull'azienda.
      if (companyId) {
        const az = NAV_ITEMS
          .filter((i) => i.group === "aziendaSelezionata" && capOk(i))
          .sort((a, b) => a.order - b.order);
        if (az.length) out.push(byGroup("aziendaSelezionata", az));
      }
    } else {
      for (const gid of ["panoramica", "ilMioEsg", "pianificazione", "reporting", "gestione"]) {
        const items = NAV_ITEMS
          .filter((i) => i.group === gid && capOk(i))
          .sort((a, b) => a.order - b.order);
        if (items.length) out.push(byGroup(gid, items));
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- capOk/ctx derivati da caps; re-run al cambio di caps/companyId/contesto
  }, [caps, companyId, isConsultantCtx]);

  // Sezione aperta di default: quella della route corrente (se nessuna preferenza).
  useEffect(() => {
    if (openSection || !sections.length) return;
    const found = sections.find((s) =>
      s.items.some((i) => location.pathname === i.route
        || location.pathname.startsWith(`${i.route}/`)));
    if (found) setOpenSection(found.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openSection intenzionalmente escluso: evita override della scelta utente
  }, [sections, location.pathname]);

  // Una sola sezione aperta alla volta (accordion): requisito mobile e desktop.
  const toggle = (gid) => {
    const next = openSection === gid ? null : gid;
    setOpenSection(next);
    api.put("/auth/preferences", { nav_open_section: next }).catch(() => {});
  };

  const renderSection = (section) => {
    const open = openSection === section.id;
    return (
      <div key={section.id}>
        <button
          type="button"
          onClick={() => toggle(section.id)}
          aria-expanded={open}
          aria-controls={`nav-panel-${section.id}`}
          data-testid={`nav-group-${section.id}`}
          className="w-full flex items-center justify-between px-3 py-1.5 mb-1 text-[11px] font-semibold tracking-wider text-slate-400 hover:text-slate-200 transition-colors rounded-md"
        >
          <span>{t(section.translationKey)}</span>
          {open
            ? <ChevronDown className="w-3 h-3" aria-hidden="true" />
            : <ChevronRight className="w-3 h-3" aria-hidden="true" />}
        </button>
        {open && (
          <ul id={`nav-panel-${section.id}`} className="space-y-1" data-testid={`nav-panel-${section.id}`}>
            {section.items.map((item) => {
              const Icon = ICONS[item.icon];
              return (
                <li key={item.id}>
                  <NavLink
                    to={item.route}
                    onClick={onNavigate}
                    data-testid={`nav-${item.id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                        isActive
                          ? "bg-green-700 text-white font-semibold"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`
                    }
                  >
                    {Icon && <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />}
                    <span className="flex-1">{t(item.translationKey)}</span>
                    {badges[item.route] ? (
                      <span className="ml-auto rounded-full bg-amber-400/90 text-slate-900 text-[10px] font-semibold px-1.5 py-0.5"
                        data-testid={`badge-nav-${item.id}`}>{badges[item.route]}</span>
                    ) : null}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-3" aria-label="Navigazione principale"
      data-testid="main-nav">
      {!caps && (
        <p className="px-3 text-xs text-slate-500" data-testid="nav-loading">Caricamento menu…</p>
      )}
      {sections.map(renderSection)}
      {showAdmin && (
        <>
        <div>
          <p className="px-3 mb-2 text-[11px] font-semibold tracking-wider text-slate-400">SUPERADMIN</p>
          {[
            ["Console", [
              ["/admin/platform", "Dashboard", LayoutDashboard, "nav-platform-console"]]],
            ["Organizzazioni", [
              ["/admin/platform/studi", "Studi", Building2, "nav-platform-studi"],
              ["/admin/platform/consulenti", "Consulenti", UserCheck, "nav-platform-consulenti"],
              ["/admin/platform/aziende", "Aziende", Leaf, "nav-platform-aziende"],
              ["/admin/platform/assegnazioni", "Assegnazioni", Link2, "nav-platform-assegnazioni"]]],
            ["Utenti e accessi", [
              ["/admin/platform/utenti", "Utenti", Users2, "nav-platform-utenti"],
              ["/admin/platform/ruoli", "Ruoli & Permessi", ShieldCheck, "nav-platform-ruoli"],
              ["/admin/platform/inviti", "Inviti", CalendarRange, "nav-platform-inviti"]]],
            ["ESG", [
              ["/admin/platform/moduli", "Moduli & Feature", Zap, "nav-platform-moduli"],
              ["/admin/normativa", "Normativa e standard", Landmark, "nav-platform-normativa"],
              ["/admin/report-templates", "Template Report", FileText, "nav-platform-templates"]]],
            ["Integrazioni", [
              ["/admin/platform/integrazioni", "JOBTIMEHR", Link2, "nav-platform-integrazioni"]]],
            ["Sistema", [
              ["/admin/sistema", "System Status", Activity, "nav-platform-system"],
              ["/admin/platform/audit", "Audit Log", ScrollText, "nav-platform-audit"],
              ["/admin/platform/importa", "Importa CSV", Upload, "nav-platform-import"],
              ["/admin/platform/tech-debt", "Tech Debt", Wrench, "nav-platform-tech-debt"],
              ["/admin/demo-requests", "Demo Requests", Inbox, "nav-platform-demo-requests"]]],
          ].map(([group, items]) => (
            <div key={group} className="mb-3">
              <p className="px-3 mb-1 text-[10px] font-semibold tracking-wider text-slate-500 uppercase">{group}</p>
              <ul className="space-y-1">
                {items.map(([to, label, Icon, tid]) => (
              <li key={tid}>
                <NavLink to={to} end={to === "/admin/platform"} onClick={onNavigate}
                  data-testid={tid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                      isActive ? "bg-green-700 text-white font-semibold" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span>{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
            </div>
          ))}
        </div>
        {user?.org_admin && (
        <div>
          <p className="px-3 mb-2 text-[11px] font-semibold tracking-wider text-slate-400">IL MIO STUDIO</p>
          <ul className="space-y-1">
            <li>
              <NavLink
                to="/studio"
                onClick={onNavigate}
                data-testid="nav-org-console"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                    isActive ? "bg-green-700 text-white font-semibold" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <Landmark className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>Console Studio</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/indicatori-mef"
                onClick={onNavigate}
                data-testid="nav-org-mef-indicators"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                    isActive ? "bg-green-700 text-white font-semibold" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <Landmark className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>Indicatori PMI–Banche</span>
              </NavLink>
            </li>
          </ul>
        </div>
        )}
        <div>
          <p className="px-3 mb-2 text-[11px] font-semibold tracking-wider text-slate-400">{t("nav.group.admin")}</p>
          <ul className="space-y-1">
            {[
              ["/admin/standard", t("admin.standard"), ShieldCheck, "nav-admin-standard"],
              ["/admin/factors", t("admin.factors"), Gauge, "nav-admin-factors"],
              ["/admin/questions", t("admin.questions"), ClipboardList, "nav-admin-questions"],
              ["/admin/frameworks", t("admin.frameworks"), Landmark, "nav-admin-frameworks"],
              ["/admin/indicatori-mef", t("admin.mefIndicators"), Landmark, "nav-admin-mef-indicators"],
              ["/admin/metodologie", t("admin.metodologie"), Scale, "nav-admin-metodologie"],
              ["/admin/eva", t("admin.eva"), Sparkles, "nav-admin-eva"],
              ["/admin/lingue", t("admin.languages"), HelpCircle, "nav-admin-lingue"],
              ["/admin/sistema", t("admin.system"), Settings, "nav-admin-sistema"],
            ].map(([to, label, Icon, tid]) => (
              <li key={tid}>
                <NavLink to={to} onClick={onNavigate}
                  data-testid={tid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                      isActive ? "bg-green-700 text-white font-semibold" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span>{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
        </>
      )}
      {!showAdmin && user?.org_admin && (
        <div>
          <p className="px-3 mb-2 text-[11px] font-semibold tracking-wider text-slate-400">IL MIO STUDIO</p>
          <ul className="space-y-1">
            <li>
              <NavLink
                to="/studio"
                onClick={onNavigate}
                data-testid="nav-org-console"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                    isActive ? "bg-green-700 text-white font-semibold" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <Landmark className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>Console Studio</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/indicatori-mef"
                onClick={onNavigate}
                data-testid="nav-org-mef-indicators"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                    isActive ? "bg-green-700 text-white font-semibold" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <Landmark className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>Indicatori PMI–Banche</span>
              </NavLink>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}

function NewCompanyDialog({ open, onOpenChange }) {
  const { refresh } = useAuth();
  const [name, setName] = useState("");
  const [piva, setPiva] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/companies", { denominazione: name, partita_iva: piva });
      await refresh();
      toast.success(t("dialog.newCompany.created"), { description: data.denominazione });
      onOpenChange(false);
      setName("");
      setPiva("");
      navigate("/azienda");
      window.location.reload();
    } catch (err) {
      toast.error(t("common.error"), { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-company-dialog">
        <DialogHeader>
          <DialogTitle>{t("dialog.newCompany.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-company-name">{t("dialog.newCompany.name")}</Label>
            <Input id="new-company-name" data-testid="new-company-name-input" value={name}
              onChange={(e) => setName(e.target.value)} required minLength={2} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-company-piva">{t("dialog.newCompany.piva")}</Label>
            <Input id="new-company-piva" data-testid="new-company-piva-input" value={piva}
              onChange={(e) => setPiva(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="new-company-cancel">{t("button.cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-green-700 hover:bg-green-800" data-testid="new-company-submit">
              {saving ? t("dialog.newCompany.creating") : t("dialog.newCompany.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout() {
  const { user, logout, refresh } = useAuth();
  const { memberships, company, companyId, changeCompany, periods, period, changePeriod, role, caps } = useTenant();
  const perms = useMemo(() => new Set(caps?.permissions || []), [caps]);
  const isConsultant = memberships.some((m) => m.role === "CONSULENTE");
  const { t } = useI18n();
  const location = useLocation();
  const tourEnabled = isConsultant && (user?.active_context || "CONSULTANT") === "CONSULTANT";
  const { goPortfolioAndStart } = useConsultantTour({ autoStart: location.pathname === "/portafoglio", enabled: tourEnabled });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Cambio esplicito di contesto (dual identity CONSULENTE/ADMIN_AZIENDA):
  // validato e auditato lato server; la selezione azienda viene resettata
  // perché il portafoglio visibile cambia con il contesto.
  const handleContextSwitch = async () => {
    const target = user?.active_context === "CONSULTANT" ? "COMPANY" : "CONSULTANT";
    try {
      await api.post("/auth/active-context", { context: target });
      localStorage.removeItem("esg_company_id");
      localStorage.removeItem("esg_period_id");
      await refresh();
      toast.success(t("header.contextSwitched"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const roleLabel = role ? t(`roleLabels.${role}`) || ROLE_LABELS[role] || role : "";
  const contextLine = [
    roleLabel,
    company?.denominazione || null,
    period ? `${t("header.fiscalYear")} ${period.year}` : null,
  ].filter(Boolean).join(" · ");

  const sidebarContent = (
    <div className="flex flex-col h-full bg-[#0B192C]">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-800">
        <div className="w-8 h-8 rounded-md bg-green-700 flex items-center justify-center">
          <Leaf className="w-4 h-4 text-white" aria-hidden="true" />
        </div>
        <div>
          <p className="text-white font-bold text-sm leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>ESG-PMI</p>
          <p className="text-slate-400 text-[11px]">Piattaforma ESG per le PMI</p>
        </div>
      </div>
      <NavItems onNavigate={() => setMobileOpen(false)} showAdmin={user?.is_superadmin} />
      <div className="p-4 border-t border-slate-800">
        <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="sidebar-context">
          {contextLine || "ESG-PMI"}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[260px] z-30" data-testid="sidebar">
        {sidebarContent}
      </aside>

      <div className="lg:pl-[260px] flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-2 px-3 md:gap-3 md:px-6 sticky top-0 z-20" data-testid="app-header">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" aria-label="Apri menu" data-testid="mobile-menu-button">
                <Menu className="w-5 h-5" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[280px] bg-[#0B192C] border-none">
              {sidebarContent}
            </SheetContent>
          </Sheet>

          {memberships.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={companyId || ""} onValueChange={changeCompany}>
                <SelectTrigger className="w-[150px] sm:w-[190px] md:w-[240px] h-9" data-testid="sidebar-company-select" aria-label="Seleziona azienda">
                  <SelectValue placeholder="Seleziona azienda" />
                </SelectTrigger>
                <SelectContent>
                  {memberships.map((m) => (
                    <SelectItem key={m.company_id} value={m.company_id} data-testid={`company-option-${m.company?.denominazione?.slice(0, 12)}`}>
                      <span className="flex items-center gap-2">
                        {m.company?.denominazione}
                        {m.company?.is_demo && <span className="text-[10px] font-bold text-amber-700">DEMO</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {company && <span className="hidden sm:inline-flex"><SizeBadge size={company.dimensione_pmi} /></span>}
              {company?.is_demo && <span className="hidden sm:inline-flex"><DemoBadge /></span>}
              {(perms.has("company:create") || perms.has("company:update")) && (
              <Button variant="ghost" size="sm" className="hidden md:flex items-center gap-1 text-green-700"
                onClick={() => setNewCompanyOpen(true)} data-testid="new-company-button">
                <Plus className="w-4 h-4" aria-hidden="true" />
                <span>{t("header.newCompany")}</span>
              </Button>
              )}
            </div>
          )}

          <div className="flex-1" />

          {periods.length > 0 && (
            <Select value={period?.id || ""} onValueChange={changePeriod}>
              <SelectTrigger className="hidden md:flex w-[210px] h-9" data-testid="reporting-period-select" aria-label={t("header.selectPeriod")}>
                <SelectValue placeholder={t("header.selectPeriod")} />
              </SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`period-option-${p.year}`}>
                    {t("header.fiscalYear")} {p.year} · {t(`periodStatus.${p.status}`) || PERIOD_STATUS[p.status]?.label || p.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Link to="/jobtimehr" data-testid="jobtimehr-status-badge"
            className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-100 border border-slate-300 rounded-full px-3 py-1 hover:bg-slate-200 transition-colors">
            <span className="w-2 h-2 rounded-full bg-slate-400" aria-hidden="true" />
            {t("header.jtNotConnected")}
          </Link>

          <LanguageSelector />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2 h-9" data-testid="user-menu-button">
                <span className="w-6 h-6 rounded-full bg-green-700 text-white text-xs font-bold flex items-center justify-center" aria-hidden="true">
                  {formatDisplayName(user?.full_name).charAt(0).toUpperCase() || "U"}
                </span>
                <span className="hidden md:inline text-sm max-w-[140px] truncate">{formatDisplayName(user?.full_name)}</span>
                <ChevronDown className="w-3 h-3 text-slate-500" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <p className="font-semibold text-slate-900">{formatDisplayName(user?.full_name)}</p>
                <p className="text-xs text-slate-500 font-normal">{user?.email}</p>
                <p className="text-xs text-green-700 font-medium mt-1">
                  {t("header.role")}: {roleLabel || "—"}
                </p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.available_contexts?.length > 1 && (
                <DropdownMenuItem onClick={handleContextSwitch} data-testid="context-switch-button">
                  <ArrowLeftRight className="w-4 h-4 mr-2" aria-hidden="true" />
                  {user?.active_context === "CONSULTANT"
                    ? t("header.switchToCompany") : t("header.switchToConsultant")}
                </DropdownMenuItem>
              )}
              {tourEnabled && (
                <DropdownMenuItem onClick={goPortfolioAndStart} data-testid="user-menu-tour">
                  <Compass className="w-4 h-4 mr-2" aria-hidden="true" /> Rivedi il tour guidato
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/impostazioni")} data-testid="user-menu-settings">
                <Settings className="w-4 h-4 mr-2" aria-hidden="true" /> {t("header.settings")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" aria-hidden="true" /> {t("header.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 md:p-8" data-testid="main-content">
          {memberships.length === 0 && location.pathname !== "/studio" ? (
            <div className="max-w-lg mx-auto mt-16 text-center" data-testid="no-company-state">
              <Building2 className="w-10 h-10 text-slate-400 mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">{t("company.empty.title")}</h2>
              <p className="text-sm text-slate-600 mb-6">{t("company.empty.text")}</p>
              <Button className="bg-green-700 hover:bg-green-800" onClick={() => setNewCompanyOpen(true)} data-testid="create-first-company-button">
                <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> {t("company.empty.cta")}
              </Button>
            </div>
          ) : (
            <>
              {tourEnabled && <div className="mb-4"><SectionHint userId={user?.id} isConsultant /></div>}
              <Outlet />
            </>
          )}
        </main>
      </div>

      {isConsultant ? (
        <CompanyWizardDialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen} mode="consultant" />
      ) : (
        <NewCompanyDialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen} />
      )}
      <FirstLanguageDialog />
      {companyId && location.pathname !== "/eva" && (
        <button
          type="button"
          onClick={() => navigate(`/eva?from=${encodeURIComponent(location.pathname)}&q=${encodeURIComponent(t("help.askEvaPageQuestion"))}`)}
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-green-700 px-4 py-2.5 text-xs font-semibold text-white shadow-lg hover:bg-green-800 transition-colors print:hidden"
          data-testid="ask-eva-page-help"
          aria-label={t("help.askEvaPage")}>
          <HelpCircle className="w-4 h-4" aria-hidden="true" />
          <span className="hidden sm:inline">{t("help.askEvaPage")}</span>
        </button>
      )}
    </div>
  );
}
